import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Box,
  Stack,
  InputAdornment,
  Grid,
  Paper,
  Divider,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  Chip,
} from '@mui/material';

// Icônes MUI
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const OrderDialog = ({
  open,
  onClose,
  offers = [],
  workshops = [],
  clients = [],
  intervenants = [],
  formData,
  setFormData,
  onSubmit,
  submitting,
}) => {
  const [errors, setErrors] = useState({});

  // 🔍 Lecture et parsing de l'objet "user" stocké dans le localStorage
  const { userRole, userId } = useMemo(() => {
    try {
      const storedUserStr = localStorage.getItem('user');
      if (storedUserStr) {
        const parsedUser = JSON.parse(storedUserStr);
        return {
          userRole: parsedUser?.role,
          userId: parsedUser?.id,
        };
      }
    } catch (e) {
      console.error('Erreur de lecture du localStorage (user) :', e);
    }
    return {
      userRole: localStorage.getItem('role'),
      userId: localStorage.getItem('user_id'),
    };
  }, []);

  // ✅ Vérification : L'utilisateur est Super Admin (role 1) ou Admin (role 2)
  const isAdmin = ['1', '2', 1, 2].includes(userRole);
  const itemType = formData.type_item || 'offre';

  // Si ce n'est pas un Admin (ex: role 4 - Praticien), on applique automatiquement son propre ID d'intervenant
  React.useEffect(() => {
    if (!isAdmin && userId && String(formData.id_intervenant) !== String(userId)) {
      setFormData((prev) => ({ ...prev, id_intervenant: userId }));
    }
  }, [isAdmin, userId, formData.id_intervenant, setFormData]);

  // 1. Filtrer les offres & ateliers selon l'intervenant ciblé
  const filteredOffers = useMemo(() => {
    const targetIntervenantId = isAdmin ? formData.id_intervenant : userId;
    if (!targetIntervenantId) return offers;
    return offers.filter(
      (o) =>
        String(o.id_intervenant) === String(targetIntervenantId) ||
        String(o.info_offre?.id_intervenant) === String(targetIntervenantId)
    );
  }, [offers, formData.id_intervenant, isAdmin, userId]);

  const filteredWorkshops = useMemo(() => {
    const targetIntervenantId = isAdmin ? formData.id_intervenant : userId;
    if (!targetIntervenantId) return workshops;
    return workshops.filter(
      (w) => String(w.id_intervenant_ws) === String(targetIntervenantId)
    );
  }, [workshops, formData.id_intervenant, isAdmin, userId]);

  // 2. Objets sélectionnés
  const selectedOfferObject = useMemo(() => {
    if (itemType === 'offre') {
      return offers.find((o) => String(o.id_offre) === String(formData.id_offre));
    }
    return null;
  }, [offers, formData.id_offre, itemType]);

  const selectedWorkshopObject = useMemo(() => {
    if (itemType === 'atelier') {
      return workshops.find((w) => String(w.id_ws) === String(formData.id_ws));
    }
    return null;
  }, [workshops, formData.id_ws, itemType]);

  // 3. Client sélectionné pour détecter s'il est patient (`is_patient == 1`)
  const selectedClientObject = useMemo(() => {
    return clients.find((c) => c.email_client === formData.email_client);
  }, [clients, formData.email_client]);

  const isClientPatient = selectedClientObject?.is_patient == 1;
  const specialSoaPrice = selectedOfferObject?.info_offre?.prix_special_soa;
  const catalogPrice = selectedOfferObject?.info_offre?.prix_offre || 0;

  // Détermine si le prix spécial SOA s'applique automatiquement
  const appliesSpecialSoa = isClientPatient && specialSoaPrice !== null && specialSoaPrice !== undefined;

  // Extraction précise de la date pour l'atelier
  const workshopSessionInfo = useMemo(() => {
    if (!selectedWorkshopObject) return null;
    const session = selectedWorkshopObject.sessions?.[0] || {};
    return {
      date: session.date_wss || selectedWorkshopObject.session_date_ws || null,
      capacity: session.capacity ?? selectedWorkshopObject.capacity_ws ?? 'Illimité',
      format: selectedWorkshopObject.format_ws || 'zoom',
    };
  }, [selectedWorkshopObject]);

  // 3. Créneaux horaires pour les offres individuelles
  const availableSlots = useMemo(() => {
    if (!selectedOfferObject || !selectedOfferObject.availability) return [];
    let slots = [];
    selectedOfferObject.availability.forEach((dispo) => {
      if (dispo.reserved_slots) {
        const valid = dispo.reserved_slots.filter(
          (slot) => slot.is_active === true && slot.status === 'available'
        );
        slots = slots.concat(valid);
      }
    });
    return slots;
  }, [selectedOfferObject]);

  const groupedSlots = useMemo(() => {
    const groups = {};
    availableSlots.forEach((slot) => {
      const dateObj = new Date(slot.start_time);
      const dateKey = dateObj.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(slot);
    });
    return groups;
  }, [availableSlots]);

  // 4. Calcul du prix affiché dynamiquement (Catalogue / Spécial SOA / Personnalisé)
  const displayedPrice = useMemo(() => {
    if (formData.prix_libre !== '' && formData.prix_libre !== undefined && formData.prix_libre !== null) {
      return { amount: formData.prix_libre, isCustom: true, label: 'Saisi manuellement' };
    }
    if (itemType === 'offre' && selectedOfferObject) {
      if (appliesSpecialSoa) {
        return { amount: specialSoaPrice, isCustom: false, label: 'Prix Spécial Patient SOA' };
      }
      return { amount: catalogPrice, isCustom: false, label: 'Prix catalogue standard' };
    }
    if (itemType === 'atelier' && selectedWorkshopObject) {
      return { amount: selectedWorkshopObject.price_ws ?? 0, isCustom: false, label: 'Prix atelier' };
    }
    return { amount: 0, isCustom: false, label: '' };
  }, [formData.prix_libre, itemType, selectedOfferObject, selectedWorkshopObject, appliesSpecialSoa, specialSoaPrice, catalogPrice]);

  const handleValidateAndSubmit = () => {
    const newErrors = {};
    if (itemType === 'offre') {
      if (!formData.id_offre) newErrors.id_offre = 'Veuillez sélectionner une séance.';
      if (!formData.date_cmd) newErrors.date_cmd = 'Veuillez choisir un créneau horaire.';
    } else {
      if (!formData.id_ws) newErrors.id_ws = 'Veuillez sélectionner un atelier.';
    }
    if (!formData.email_client) {
      newErrors.email_client = 'Veuillez attribuer un client.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      onSubmit();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          color: '#2B3674',
          boxShadow: '0px 24px 60px rgba(112, 144, 176, 0.2)',
          border: '1px solid #E0E5F2',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          px: 4,
          bgcolor: '#F4F7FE',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #E0E5F2',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.45rem', color: '#2B3674' }}>
            Création d'une commande & réservation
          </Typography>
          <Typography variant="body2" sx={{ color: '#A3AED0', mt: 0.5, fontWeight: 500 }}>
            Choisissez la prestation, l'horaire et attribuez un client.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#A3AED0',
            bgcolor: '#FFFFFF',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
            '&:hover': { bgcolor: '#F4F7FE', color: '#2B3674' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Type de prestation */}
        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#2B3674', mb: 1 }}>
            Type de prestation *
          </Typography>
          <ToggleButtonGroup
            value={itemType}
            exclusive
            onChange={(e, newType) => {
              if (newType !== null) {
                setFormData({
                  ...formData,
                  type_item: newType,
                  id_offre: '',
                  id_ws: '',
                  date_cmd: '',
                });
              }
            }}
            fullWidth
            sx={{
              bgcolor: '#F4F7FE',
              p: 0.5,
              borderRadius: '14px',
              border: '1px solid #E0E5F2',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '10px !important',
                py: 1.2,
                fontWeight: 700,
                textTransform: 'none',
                color: '#A3AED0',
                '&.Mui-selected': {
                  bgcolor: '#FFFFFF',
                  color: '#4318FF',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                },
              },
            }}
          >
            <ToggleButton value="offre">
              <PersonOutlinedIcon sx={{ mr: 1 }} /> Séance Individuelle (Offre)
            </ToggleButton>
            <ToggleButton value="atelier">
              <GroupsOutlinedIcon sx={{ mr: 1 }} /> Atelier Collectif
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={3}>
          {/* Colonne de gauche : Intervenant, Offre/Atelier, Prix, Client */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2.5}>
              {/* INTERVENANT (Si Admin) */}
              {isAdmin && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PersonOutlineIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                      Choix de l'Acteur / Intervenant{' '}
                      <span style={{ color: '#A3AED0', fontWeight: 500 }}>(Optionnel)</span>
                    </Typography>
                  </Stack>
                  <Autocomplete
                    options={intervenants}
                    getOptionLabel={(option) =>
                      `${option.nom_user || ''} ${option.prenom_user || ''}`.trim() || option.email_user || ''
                    }
                    value={
                      intervenants.find(
                        (i) => String(i.id_user) === String(formData.id_intervenant)
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        id_intervenant: newValue ? newValue.id_user : '',
                        id_offre: '',
                        id_ws: '',
                        date_cmd: '',
                      });
                    }}
                    noOptionsText="Aucun intervenant disponible"
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id_user}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {option.photo_user && (
                            <Avatar
                              src={`${process.env.REACT_APP_API_URL}/storage/workshop/${option.photo_user}`}
                              sx={{ width: 26, height: 26 }}
                            />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2B3674' }}>
                            {option.nom_user} {option.prenom_user}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Tous les intervenants..."
                        sx={{
                          bgcolor: '#F4F7FE',
                          borderRadius: '12px',
                          '& .MuiOutlinedInput-root': {
                            color: '#2B3674',
                            fontWeight: 600,
                            borderRadius: '12px',
                            '& fieldset': { borderColor: '#E0E5F2' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* CLIENT ATTRIBUÉ (Placé ici stratégiquement pour détecter s'il est patient en amont) */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <PersonOutlineIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                    Client Attribué *
                  </Typography>
                </Stack>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) =>
                    `${option.nom_user || option.nom_client || ''} ${option.prenom_user || option.prenom_client || ''} (${option.email_client || option.email || ''})`
                  }
                  value={selectedClientObject || null}
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      email_client: newValue ? (newValue.email_client || newValue.email) : '',
                    });
                    if (errors.email_client) setErrors({ ...errors, email_client: null });
                  }}
                  noOptionsText="Aucun client trouvé"
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id_client || option.id_user || option.email_client}>
                      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-width" sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2B3674' }}>
                          {option.nom_client || option.nom_user} {option.prenom_client || option.prenom_user} ({option.email_client || option.email})
                        </Typography>
                        {option.is_patient == 1 && (
                          <Chip label="Patient" size="small" sx={{ bgcolor: '#E9E3FF', color: '#4318FF', fontWeight: 700, height: '20px' }} />
                        )}
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Rechercher un client par nom ou email..."
                      error={Boolean(errors.email_client)}
                      helperText={errors.email_client}
                      sx={{
                        bgcolor: '#F4F7FE',
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-root': {
                          color: '#2B3674',
                          fontWeight: 600,
                          borderRadius: '12px',
                          '& fieldset': { borderColor: errors.email_client ? '#EE5D50' : '#E0E5F2' },
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* CHOIX OFFRE OU ATELIER */}
              {itemType === 'offre' ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <LocalOfferOutlinedIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                      Sélectionner la Séance / Offre *
                    </Typography>
                  </Stack>
                  <Autocomplete
                    options={filteredOffers}
                    getOptionLabel={(option) =>
                      `${option.info_offre?.titre_offre || ''} (${isClientPatient && option.info_offre?.prix_special_soa ? option.info_offre?.prix_special_soa : option.info_offre?.prix_offre || 0} €)`
                    }
                    value={selectedOfferObject || null}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        id_offre: newValue ? newValue.id_offre : '',
                        date_cmd: '',
                      });
                      if (errors.id_offre) setErrors({ ...errors, id_offre: null });
                    }}
                    noOptionsText={formData.id_intervenant || userId ? "Aucune offre pour cet acteur" : "Aucune offre trouvée"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Rechercher une séance..."
                        error={Boolean(errors.id_offre)}
                        helperText={errors.id_offre}
                        sx={{
                          bgcolor: '#F4F7FE',
                          borderRadius: '12px',
                          '& .MuiOutlinedInput-root': {
                            color: '#2B3674',
                            fontWeight: 600,
                            borderRadius: '12px',
                            '& fieldset': { borderColor: errors.id_offre ? '#EE5D50' : '#E0E5F2' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              ) : (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <GroupsOutlinedIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                      Sélectionner l'Atelier *
                    </Typography>
                  </Stack>
                  <Autocomplete
                    options={filteredWorkshops}
                    getOptionLabel={(option) => `${option.title_ws || ''} (${option.price_ws ?? 0}€)`}
                    value={selectedWorkshopObject || null}
                    onChange={(event, newValue) => {
                      const extractedDate = newValue?.sessions?.[0]?.date_wss || newValue?.session_date_ws || '';
                      setFormData({
                        ...formData,
                        id_ws: newValue ? newValue.id_ws : '',
                        date_cmd: extractedDate,
                      });
                      if (errors.id_ws) setErrors({ ...errors, id_ws: null });
                    }}
                    noOptionsText={formData.id_intervenant || userId ? "Aucun atelier pour cet acteur" : "Aucun atelier trouvé"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Rechercher un atelier..."
                        error={Boolean(errors.id_ws)}
                        helperText={errors.id_ws}
                        sx={{
                          bgcolor: '#F4F7FE',
                          borderRadius: '12px',
                          '& .MuiOutlinedInput-root': {
                            color: '#2B3674',
                            fontWeight: 600,
                            borderRadius: '12px',
                            '& fieldset': { borderColor: errors.id_ws ? '#EE5D50' : '#E0E5F2' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* TARIFICATION APPLIQUÉE (PRIX LIBRE / SPÉCIAL SOA) */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PaymentsOutlinedIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                      Tarification appliquée (Prix Libre)
                    </Typography>
                  </Stack>
                  {appliesSpecialSoa && (
                    <Chip
                      icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important', color: '#4318FF !important' }} />}
                      label="Tarif Patient SOA Activé"
                      size="small"
                      sx={{ bgcolor: '#E9E3FF', color: '#4318FF', fontWeight: 700, fontSize: '11px', height: '22px' }}
                    />
                  )}
                </Stack>
                <TextField
                  fullWidth
                  type="number"
                  placeholder={
                    appliesSpecialSoa
                      ? `Prix spécial SOA : ${specialSoaPrice}€`
                      : displayedPrice.amount > 0
                      ? `Ex : ${displayedPrice.amount}`
                      : 'Saisissez le montant'
                  }
                  value={formData.prix_libre || ''}
                  onChange={(e) => setFormData({ ...formData, prix_libre: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ fontWeight: 700, color: '#2B3674' }}>€</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: '#F4F7FE',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-root': {
                      color: '#2B3674',
                      fontWeight: 700,
                      borderRadius: '12px',
                      '& fieldset': { borderColor: appliesSpecialSoa ? '#4318FF' : '#E0E5F2' },
                    },
                  }}
                />
                <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 1 }}>
                  <InfoOutlinedIcon sx={{ color: '#A3AED0', fontSize: '15px', mt: '2px' }} />
                  <Typography variant="caption" sx={{ color: '#A3AED0', lineHeight: 1.3 }}>
                    {appliesSpecialSoa ? (
                      <>
                        Le client sélectionné est un <strong>Patient</strong>. Le prix spécial SOA de <strong>{specialSoaPrice}€</strong> est appliqué par défaut (sans commission).
                      </>
                    ) : (
                      <>
                        Si ce champ reste vide, le tarif catalogue (<strong>{catalogPrice}€</strong>) sera pris en compte.
                      </>
                    )}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          {/* Colonne de droite : Créneaux horaires / Infos atelier + Résumé */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2.5}>
              {itemType === 'offre' ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <EventAvailableIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                      Date & Créneau Horaire *
                    </Typography>
                  </Stack>
                  {!formData.id_offre ? (
                    <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#F4F7FE', borderRadius: '16px', border: '1px dashed #E0E5F2' }}>
                      <Typography variant="body2" sx={{ color: '#A3AED0', fontWeight: 600 }}>
                        Veuillez d'abord choisir une séance pour afficher son planning.
                      </Typography>
                    </Paper>
                  ) : Object.keys(groupedSlots).length === 0 ? (
                    <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#FFF5F5', borderRadius: '16px', border: '1px solid #FFE0E0' }}>
                      <Typography variant="body2" sx={{ color: '#EE5D50', fontWeight: 600 }}>
                        Aucun créneau libre disponible pour cette séance.
                      </Typography>
                    </Paper>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: '260px',
                        overflowY: 'auto',
                        pr: 1,
                        '&::-webkit-scrollbar': { width: '5px' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: '#E0E5F2', borderRadius: '10px' },
                      }}
                    >
                      <Stack spacing={2}>
                        {Object.entries(groupedSlots).map(([dateLabel, slots]) => (
                          <Box key={dateLabel}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 800, textTransform: 'capitalize', color: '#4318FF', mb: 0.8 }}>
                              📅 {dateLabel}
                            </Typography>
                            <Grid container spacing={1}>
                              {slots.map((slot) => {
                                const dateObj = new Date(slot.start_time);
                                const isSelected = formData.date_cmd === slot.start_time;
                                const timeStr = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

                                return (
                                  <Grid item xs={4} key={slot.id_reserved_slot}>
                                    <Button
                                      fullWidth
                                      variant={isSelected ? 'contained' : 'outlined'}
                                      onClick={() => {
                                        setFormData({ ...formData, date_cmd: slot.start_time });
                                        if (errors.date_cmd) setErrors({ ...errors, date_cmd: null });
                                      }}
                                      sx={{
                                        py: 0.8,
                                        borderRadius: '10px',
                                        fontWeight: 800,
                                        fontSize: '13px',
                                        textTransform: 'none',
                                        bgcolor: isSelected ? '#4318FF' : '#FFFFFF',
                                        color: isSelected ? '#FFFFFF' : '#2B3674',
                                        borderColor: isSelected ? '#4318FF' : '#E0E5F2',
                                        boxShadow: isSelected ? '0px 4px 12px rgb(67 24 255 / .3)' : 'none',
                                        '&:hover': { bgcolor: isSelected ? '#3311CC' : '#F4F7FE', borderColor: '#4318FF' },
                                      }}
                                    >
                                      {timeStr}
                                    </Button>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {errors.date_cmd && (
                    <Typography variant="caption" sx={{ color: '#EE5D50', mt: 1, display: 'block', fontWeight: 600 }}>
                      {errors.date_cmd}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <EventAvailableIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#2B3674' }}>
                      Session & Détails de l'Atelier
                    </Typography>
                  </Stack>
                  {!selectedWorkshopObject ? (
                    <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#F4F7FE', borderRadius: '16px', border: '1px dashed #E0E5F2' }}>
                      <Typography variant="body2" sx={{ color: '#A3AED0', fontWeight: 600 }}>
                        Sélectionnez un atelier pour consulter ses informations.
                      </Typography>
                    </Paper>
                  ) : (
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#F4F7FE', border: '1px solid #E0E5F2' }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Chip
                            icon={<VideocamIcon sx={{ fontSize: '16px !important' }} />}
                            label={workshopSessionInfo?.format?.toUpperCase() || 'ZOOM'}
                            size="small"
                            sx={{ bgcolor: '#E9E3FF', color: '#4318FF', fontWeight: 800, fontSize: '11px' }}
                          />
                          <Chip
                            label={`Capacité : ${workshopSessionInfo?.capacity} pers.`}
                            size="small"
                            sx={{ bgcolor: '#E6F9F0', color: '#05CD99', fontWeight: 700, fontSize: '11px' }}
                          />
                        </Stack>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#A3AED0', fontWeight: 700, textTransform: 'uppercase' }}>
                            Date & Heure de la Session
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                            <AccessTimeIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                            <Typography sx={{ fontWeight: 800, color: '#2B3674', fontSize: '14px' }}>
                              {workshopSessionInfo?.date
                                ? new Date(workshopSessionInfo.date.replace(' ', 'T')).toLocaleString('fr-FR', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'Date non définie'}
                            </Typography>
                          </Stack>
                        </Box>
                        {selectedWorkshopObject.address_ws && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#A3AED0', fontWeight: 700, textTransform: 'uppercase' }}>
                              Lieu / Adresse
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.3 }}>
                              <LocationOnOutlinedIcon sx={{ color: '#4318FF', fontSize: '18px' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2B3674' }}>
                                {selectedWorkshopObject.address_ws}, {selectedWorkshopObject.city_ws || ''}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Résumé de la Facturation */}
              <Box sx={{ mt: 'auto' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '16px',
                    bgcolor: '#F4F7FE',
                    border: '1px solid #E0E5F2',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#2B3674', fontSize: '14px' }}>
                        Résumé de la Facturation
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#A3AED0', display: 'block', mt: 0.3 }}>
                        {displayedPrice.label}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography sx={{ color: '#A3AED0', fontWeight: 600, fontSize: '13px' }}>
                        Montant Final :
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '22px',
                          fontWeight: 800,
                          color: displayedPrice.isCustom || appliesSpecialSoa ? '#4318FF' : '#05CD99',
                        }}
                      >
                        {displayedPrice.amount}€
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider sx={{ borderColor: '#E0E5F2' }} />

      <DialogActions
        sx={{
          p: 2.5,
          px: 4,
          bgcolor: '#FFFFFF',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            color: '#A3AED0',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            py: 1,
            borderRadius: '12px',
            '&:hover': { bgcolor: '#F4F7FE', color: '#2B3674' },
          }}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={handleValidateAndSubmit}
          sx={{
            bgcolor: '#4318FF',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 700,
            px: 4,
            py: 1.2,
            borderRadius: '12px',
            boxShadow: '0px 8px 16px rgba(67, 24, 255, 0.24)',
            '&:hover': { bgcolor: '#3311CC' },
          }}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Créer la commande'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDialog;